import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';

declare const __APP_VERSION__: string;

interface CacheBusterState {
  loading: boolean;
  isLatestVersion: boolean;
}

function isNewerVersion(server: string, local: string): boolean {
  const a = server.split('.').map(Number);
  const b = local.split('.').map(Number);

  for (let i = 0; i < Math.max(a.length, b.length); i++) {
    const sa = a[i] ?? 0;
    const sb = b[i] ?? 0;
    if (sa !== sb) return sa > sb;
  }

  return false;
}

function clearCacheAndReload() {
  if (globalThis.caches) {
    globalThis.caches.keys().then((names) => {
      for (const name of names) globalThis.caches.delete(name);
    });
  }
  window.location.reload();
}

interface CacheBusterProps {
  children: (state: CacheBusterState) => ReactNode;
}

export default function CacheBuster({ children }: CacheBusterProps) {
  const [state, setState] = useState<CacheBusterState>({
    loading: true,
    isLatestVersion: false,
  });

  useEffect(() => {
    // Skip the version check in dev: __APP_VERSION__ is frozen at Vite startup
    // while public/meta.json may bump (via commit hooks), causing a reload loop.
    if (import.meta.env.DEV) {
      setState({ loading: false, isLatestVersion: true });
      return;
    }

    fetch(`/meta.json?nocache=${Date.now()}`)
      .then((res) => res.json())
      .then((meta: { version: string }) => {
        const currentVersion = __APP_VERSION__;
        const shouldRefresh = isNewerVersion(meta.version, currentVersion);

        if (shouldRefresh) {
          console.log(`New version available: ${meta.version} (current: ${currentVersion}). Refreshing...`);
          clearCacheAndReload();
        } else {
          console.log(`App is up to date — v${currentVersion}`);
          setState({ loading: false, isLatestVersion: true });
        }
      })
      .catch(() => {
        // If meta.json fails (e.g. dev mode), proceed normally
        setState({ loading: false, isLatestVersion: true });
      });
  }, []);

  if (state.loading) return null;

  return <>{children(state)}</>;
}
